// RowOverflow — the ONE lifecycle overflow affordance (master audit R5).
//
// A kebab IconButton opening the row's action Menu (Byt namn / Ta bort /
// kind-specific extras). The same pattern everywhere an object can be acted
// on — rail rows and object-view toolbars — so removal stops being a per-view
// one-off (lifecycle symmetry; user control & freedom, Nielsen #3).
//
// Reveal discipline: the trigger is VISUALLY quiet until the row is engaged
// (hover / focus-within / menu open — see RowOverflow.css) but always in the
// a11y tree and keyboard-reachable; opacity, never display:none, so a Tab
// stop or AT never loses it. The consumer adds `bilda-row-overflow-host` to
// the row container that defines the hover scope; without that class the
// kebab simply stays visible (the object-view toolbars want exactly that).
//
// Pure/props-driven: the action list arrives as Menu's own typed `MenuItem`
// sum (types over raw objects); every selection reports UP via the item's
// `onSelect`. No state, no I/O; Radix owns the menu a11y via Menu.

import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { Menu, type MenuItem } from "../Menu/Menu.js";
import "./RowOverflow.css";

export type RowOverflowProps = {
    // The kebab's accessible name — names the OBJECT, not just "actions"
    // (e.g. "Åtgärder för <title>"), caller-owned and localised.
    readonly label: string;
    // The row's actions, in Menu's typed vocabulary.
    readonly items: readonly MenuItem[];
    readonly className?: string;
    // -1 takes the kebab out of the tab order (a list that keeps one tab stop
    // per row: only the marked row's kebab is tabbable).
    readonly tabIndex?: number;
    // A class on the opened menu (see Menu's `contentClassName`).
    readonly menuClassName?: string;
};

export const RowOverflow = ({
    label,
    items,
    className,
    tabIndex,
    menuClassName,
}: RowOverflowProps) => (
    <span className={`bilda-row-overflow${className ? ` ${className}` : ""}`}>
        <Menu
            align="end"
            contentClassName={menuClassName}
            trigger={
                <IconButton
                    label={label}
                    variant="ghost"
                    size="sm"
                    className="bilda-row-overflow__trigger"
                    tabIndex={tabIndex}
                >
                    {/* The ONE row-overflow glyph — the Icon registry
                        owns the vertical kebab; decorative, the
                        IconButton's label names the control. */}
                    <Icon name="overflow" />
                </IconButton>
            }
            items={items}
        />
    </span>
);
